import type {
  LetterHolderRole,
  AwardLetterLine,
  AwardLetters,
  LetterGap,
  LetterLineStatus,
  LetterPersonState,
  LetterReview,
  LetterTemplateNote,
  LetterTemplateRef,
  QueueRow,
} from '@myrewards/contracts';
import { isApiError } from '../../api';
import {
  formatDate,
  formatShortDate,
  plural,
  type ConfirmSummaryRow,
  type IconName,
  type StatusTone,
} from '../../ui';
import { problemWords, TYPE_LABEL, TYPE_SHORT } from '../labels';

// The letters step (SA3) in plain words: each person's letter, who holds it, and what releasing
// them all sets off. Plain stages only; no internal state names reach the page.

type Line = AwardLetterLine;

/** Where the letter templates are kept: the Settings helper's elections and letters screen. */
export const TEMPLATES_HREF = '/settings/elections-letters';

export const LINE_STATUS: Record<LetterLineStatus, { label: string; tone: StatusTone }> = {
  missing: { label: 'Not made yet', tone: 'neutral' },
  checking: { label: 'Checking for viruses', tone: 'waiting' },
  ready: { label: 'Ready', tone: 'done' },
  stale: { label: 'Out of date', tone: 'problem' },
  blocked: { label: 'Blocked by the virus check', tone: 'problem' },
  released: { label: 'Released', tone: 'done' },
};

/**
 * APP-130: each person's letter as people see it, in words with an icon (status is never shown by
 * colour alone). Reviewing is encouraged, never required: Ready to read can go out as it is.
 */
export const PERSON_STATE: Record<
  LetterPersonState,
  { label: string; tone: StatusTone; icon: IconName }
> = {
  waiting: { label: 'Waiting on a template', tone: 'waiting', icon: 'clock' },
  missing: { label: 'Not made yet', tone: 'neutral', icon: 'file' },
  checking: { label: 'Checking for viruses', tone: 'waiting', icon: 'shield' },
  ready_to_read: { label: 'Ready to read', tone: 'info', icon: 'eye' },
  reviewed: { label: 'Reviewed', tone: 'done', icon: 'check' },
  something_wrong: { label: 'Something’s wrong', tone: 'problem', icon: 'alert' },
  stale: { label: 'Out of date', tone: 'problem', icon: 'refresh' },
  blocked: { label: 'Blocked by the virus check', tone: 'problem', icon: 'shield' },
  released: { label: 'Released', tone: 'done', icon: 'send' },
};

/** A person's state, read from the server; from the letter's status where it sends none. */
export function personStateOf(line: Line): LetterPersonState {
  if (line.state !== undefined) return line.state;
  return line.status === 'ready' ? 'ready_to_read' : line.status;
}

/** "Cash only", "Cash and options". */
export const MIX_WORDS: Record<LetterGap['mix'], string> = {
  cash_only: 'Cash only',
  options_only: 'Options only',
  cash_and_options: 'Cash and options',
};

/** "Jordan’s letter waits for it.", "3 people’s letters wait for it: Jordan, Sam and Ali." */
export function gapPeopleWords(gap: LetterGap): string {
  const names = gap.people.map((p) => firstName(p.name));
  // With a stand-in in use, the letters are made from it meanwhile (D7, D9).
  if (gap.fallback?.usable === true)
    return names.length === 1
      ? `${names[0] ?? 'One person'}’s letter uses the stand-in for now.`
      : `${plural(names.length, 'person’s letter uses', 'people’s letters use')} the stand-in for now: ${andList(names)}.`;
  // APP-212: with no stand-in, the built-in sample letter is made meanwhile; nobody waits.
  if (gap.builtInSample != null)
    return names.length === 1
      ? `${names[0] ?? 'One person'}’s letter is the built-in sample for now.`
      : `${plural(names.length, 'person’s letter is', 'people’s letters are')} the built-in sample for now: ${andList(names)}.`;
  if (names.length === 1) return `${names[0] ?? 'One person'}’s letter waits for it.`;
  return `${plural(names.length, 'person’s letter waits', 'people’s letters wait')} for it: ${andList(names)}.`;
}

/**
 * APP-130 D104: a gap card's title. "Jordan’s letter is waiting on a template", "3 people’s letters
 * are waiting on a template", and with a stand-in in use, the stand-in's words from
 * `gapPeopleWords` ("Jordan’s letter uses the stand-in for now"). First names, as the page's other
 * sentences about a person; the card lists them in full.
 */
export function gapTitle(gap: LetterGap): string {
  const [only] = gap.people;
  const standIn = gap.fallback?.usable === true;
  // APP-212: with no stand-in, the built-in sample letter fills the gap for now.
  const sample = !standIn && gap.builtInSample != null;
  if (gap.people.length === 1 && only !== undefined)
    return `${firstName(only.name)}’s letter ${standIn ? 'uses the stand-in for now' : sample ? 'is the built-in sample for now' : 'is waiting on a template'}`;
  return standIn
    ? `${plural(gap.people.length, 'person’s letter uses', 'people’s letters use')} the stand-in for now`
    : sample
      ? `${plural(gap.people.length, 'person’s letter is', 'people’s letters are')} the built-in sample for now`
      : `${plural(gap.people.length, 'person’s letter is', 'people’s letters are')} waiting on a template`;
}

/** "Jordan Ellis, Sam Patel and Ali Khan": who is in a gap, in full. */
export function gapNames(gap: LetterGap): string {
  return andList(gap.people.map((p) => p.name));
}

/** "Jordan, Sam and Ali". */
export function andList(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

/** D8: "Signed by Robin Diaz, not Avery Chen", or "Signed by Avery Chen" when it's the same person. */
export function signedByWords(
  template: Pick<LetterTemplateRef, 'signer'>,
  expected: { id: string; name: string },
): string {
  const signer = template.signer;
  if (signer === undefined) return `Not signed by ${expected.name}`;
  return signer.id === expected.id
    ? `Signed by ${signer.name}`
    : `Signed by ${signer.name}, not ${expected.name}`;
}

/** The version people see: the design's own number (D2), else the upload number. */
export function shownVersion(t: Pick<LetterTemplateRef, 'version' | 'designVersion'>): string {
  return `v${String(t.designVersion ?? t.version)}`;
}

/** An approved letter that can stand in, as the gap card lists it. */
export function candidateLabel(t: LetterTemplateRef): string {
  return `${t.name} ${shownVersion(t)}${t.signer ? ` · signed by ${t.signer.name}` : ''}`;
}

/** Who said what about a letter, and when, in one sentence. */
export function reviewWords(review: LetterReview): string {
  const when = formatShortDate(review.at);
  switch (review.verdict) {
    case 'looks_right':
      return `${review.by.name} read it on ${when}: looks right.${review.note ? ` “${review.note}”` : ''}`;
    case 'something_wrong':
      return `${review.by.name} marked it Something’s wrong on ${when}: “${review.note ?? ''}”`;
    case 'cleared':
      return `${review.by.name} cleared it on ${when}: “${review.note ?? ''}”${review.answers ? ` It had been marked Something’s wrong: “${review.answers.note ?? ''}”` : ''}`;
  }
}

/** People who can read this person's letter now: made, current and through its virus check. */
export function reviewable(line: Line): boolean {
  const state = personStateOf(line);
  return state === 'ready_to_read' || state === 'reviewed' || state === 'something_wrong';
}

/** "Check it against the award": what to say when every fact matches. */
export function checkSummary(line: Line): string {
  const rows = line.check ?? [];
  const differ = rows.filter((r) => r.differs).length;
  if (rows.length === 0) return 'Make the letter first, then check it against the award.';
  return differ === 0
    ? 'Every fact in the letter matches the award.'
    : `${plural(differ, 'fact differs', 'facts differ')} from the award now.`;
}

/** The first name, for sentences about the person ("Jordan sees the award …"). */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "Letter for Jordan Ellis", "Letters for 3 people". */
export function lettersTitle(letters: AwardLetters): string {
  const [only] = letters.lines;
  if (letters.lines.length === 1 && only !== undefined) return `Letter for ${only.person.name}`;
  return `Letters for ${plural(letters.lines.length, 'person', 'people')}`;
}

/** "PAA template v3 · Word", with "(default)" for the type's default. */
export function templateLabel(t: LetterTemplateRef, isDefault: boolean): string {
  const format = t.format === 'docx' ? 'Word' : 'PDF';
  return `${t.name} ${shownVersion(t)} · ${format}${isDefault ? ' (default)' : ''}`;
}

/** The templates to pick from: the default first. */
export function templateOptions(
  letters: AwardLetters,
  lineId: string | null = null,
): { id: string; label: string }[] {
  const lines =
    lineId === null ? letters.lines : letters.lines.filter((line) => line.lineId === lineId);
  const variants = {
    any: 'Any amounts',
    cash_only: 'Cash only',
    options_only: 'Options only',
    cash_and_options: 'Cash and options',
  };
  const defaultId = letters.templates.default?.id;
  const sorted = letters.templates.available
    .filter(
      (t) =>
        (t.status === 'approved' || t.status === 'default') &&
        lines.every(
          (line) =>
            t.variant === undefined ||
            t.variant === 'any' ||
            t.variant ===
              (line.cashCents > 0 && line.options > 0
                ? 'cash_and_options'
                : line.options > 0
                  ? 'options_only'
                  : 'cash_only'),
        ),
    )
    .sort((a, b) => Number(b.id === defaultId) - Number(a.id === defaultId));
  // APP-272 (FB-2026-0034): a letter's name once. Its signer and mix only where the name doesn't
  // already say them, and its format only where that's what tells two apart.
  const named = (t: LetterTemplateRef) => `${t.name} ${shownVersion(t)}`;
  const alike = (t: LetterTemplateRef) => sorted.filter((o) => named(o) === named(t)).length > 1;
  return sorted.map((t) => {
    const own = t.name.toLocaleLowerCase('en-US');
    const says = (words: string) => own.includes(words.toLocaleLowerCase('en-US'));
    const format = alike(t) ? ` · ${t.format === 'docx' ? 'Word' : 'PDF'}` : '';
    const signer = t.signer && !says(t.signer.name) ? ` · ${t.signer.name}` : '';
    const mix = t.variant && !says(variants[t.variant]) ? ` · ${variants[t.variant]}` : '';
    const isDefault = t.id === defaultId ? ' (default)' : '';
    return { id: t.id, label: `${named(t)}${format}${isDefault}${signer}${mix}` };
  });
}

/** Plain words for the facts a template fills in, by merge field name. */
const FIELD_WORDS: Record<string, string> = {
  employeeName: 'name',
  employeeFirstName: 'first name',
  employeeLegalName: 'name',
  employeeNumber: 'employee number',
  awardName: 'award name',
  cash: 'cash',
  options: 'options',
  vesting: 'vesting',
  awardDate: 'award date',
  signerName: 'signer',
  signer: 'signer',
  electionDeadline: 'election deadline',
  electionDeadlineDate: 'election deadline',
  taxEstimates: 'estimated tax',
  recipient: 'letter holder',
};

/** "Changed since it was made: employee number and cash." */
export function staleWords(fields: readonly string[]): string {
  const words = [...new Set(fields.map((f) => FIELD_WORDS[f] ?? 'other details'))];
  if (words.length === 0) return 'Something on the award changed since it was made.';
  const list =
    words.length === 1
      ? (words[0] ?? '')
      : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
  return `Changed since it was made: ${list}.`;
}

/**
 * APP-130 D22: a letter refused for a doubled word keeps its field and words with its code
 * (`letter_doubled_word:publicCitation:your your`), so the person sees what the letter would say.
 */
const DOUBLED_WORD = 'letter_doubled_word';
function doubledWordCode(field: string, words: string): string {
  return `${DOUBLED_WORD}:${field}:${words}`;
}
function doubledWordWords(code: string, first: string, wording = 'award wording'): string {
  const [, field = '', ...rest] = code.split(':');
  const words = rest.join(':');
  if (words === '')
    return `${first}’s letter would print a word twice next to a filled-in detail. Change the ${wording} or the template, then generate again.`;
  if (field === 'publicCitation')
    return `${first}’s letter would read “${words}”. Take the extra word out of the ${wording}, or out of the template, then generate again.`;
  return `${first}’s letter would read “${words}” beside the ${FIELD_WORDS[field] ?? 'filled-in details'}. Take the extra word out of the template, then generate again.`;
}

/** Whether an issue code is a doubled word (D22), with or without its field and words. */
export function isDoubledWord(code: string): boolean {
  return code === DOUBLED_WORD || code.startsWith(`${DOUBLED_WORD}:`);
}

/**
 * APP-237: a refused letter the recipient citation fixes (no citation yet, or a word it would
 * print twice), so a Spot letter page offers the citation box whatever the template.
 */
export function fixedInCitation(code: string): boolean {
  return code === 'citation_required' || code.startsWith(`${DOUBLED_WORD}:publicCitation:`);
}

/**
 * A doubled word in someone's letter, in the same words as an award's Letters page (D22), naming
 * the page's own field for the wording: a Spot letter's is the recipient citation.
 */
export function doubledWordText(code: string, name: string, wording?: string): string {
  return doubledWordWords(code, firstName(name), wording);
}

/**
 * Where the award wording sits in the letter, so it reads as one sentence (APP-130 D21). Only the
 * lead-ins the client's letters are known to use are named: the PAA letter's (D21) and the
 * Executive bonus letter's (QA-071). A template whose check found "your" or "the" just before the
 * wording says so; any other letter gets the general hint.
 */
export function wordingHint(
  type: AwardLetters['type'],
  notes: readonly LetterTemplateNote[] = [],
): string {
  if (notes.includes('article_before_citation'))
    return 'This letter already puts a word such as “your” or “the” just before the wording, so begin with the effort itself.';
  if (type === 'PAA')
    return 'It follows “for your participation with” in the PAA letter. Begin with the effort, not “your”.';
  if (type === 'EDB')
    return 'It follows “in recognition of” in the Executive bonus letter, so it usually begins with “your”.';
  return 'It follows the letter’s own words. Begin so the two read as one sentence, without repeating a word.';
}

/**
 * APP-246: what to know about a person's letter that never stops it being made or released, in
 * plain words; null for a note this page doesn't know, which it leaves out.
 */
export function noteWords(code: string, name: string): string | null {
  switch (code) {
    case 'employee_number_missing':
      return `${name} has no employee number in the directory, so the letter is made without one. The employee number comes from Entra ID: if they should have one, ask whoever manages Entra ID (your IT team) to add it, then make the letter again.`;
    default:
      return null;
  }
}

/** APP-246: a person's letter notes, in words, each once. */
export function lineNotes(line: Pick<Line, 'notes' | 'person'>): string[] {
  return [...new Set((line.notes ?? []).map((n) => n.code))].flatMap((code) => {
    const words = noteWords(code, line.person.name);
    return words === null ? [] : [words];
  });
}

/** What must be fixed before a person's letter can be made or released, in plain words. */
export function issueWords(code: string, name: string): string {
  const first = firstName(name);
  if (isDoubledWord(code)) return doubledWordWords(code, first);
  switch (code) {
    case 'award_date_required':
      return 'Set the award date first: every letter prints it.';
    case 'letter_signer_missing':
    case 'letter_signer_ambiguous':
    case 'signer_missing':
      return 'The signing policy could not resolve a signer. Check the letter award mappings.';
    case 'letter_mapping_missing':
    case 'template_missing':
      return 'No approved template matches this signer and amount variant. Check the letter award mappings.';
    case 'letter_mapping_ambiguous':
    case 'template_ambiguous':
      return 'Several approved templates match. Keep one matching version in the letter award mappings.';
    case 'letter_override_reason_required':
      return 'Enter a reason for selecting another template.';
    case 'letter_override_incompatible':
      return 'Choose an approved template for this award type and amount variant.';
    case 'public_citation_required':
      return 'Enter the public award wording before generating this letter.';
    case 'citation_required':
      // APP-201: one wording for the whole award, set above the letters.
      return 'The award has no letter wording yet. Add it under Letter wording, then generate again.';
    case 'signer_required':
      return 'The template has no signer. Choose one in Settings › Elections and letters.';
    case 'signer_inactive':
      return 'The template’s signer is no longer active. Choose another in Settings › Elections and letters.';
    case 'tax_estimates_missing':
      return `${taxMissingWords(name)} ${TAX_TO_ENTER_LINK}.`;
    case 'election_deadline_missing':
      return `${first}’s election deadline couldn’t be worked out. Check the award date, then generate again.`;
    case 'recipient_required':
      return `Choose who gets ${first}’s letter.`;
    // SENT-239: there's no name to use here, and only the directory can fix it.
    case 'employee_name_missing':
      return 'This person has no name in the directory. Fix it there, then generate the letters again.';
    case 'letter_missing':
      return `${first}’s letter hasn’t been made yet. Generate the letters.`;
    case 'letter_checking':
      return `${first}’s letter is still being checked for viruses. It’s usually ready in a minute.`;
    case 'letter_stale':
      return `Something changed since ${first}’s letter was made. Generate the letters again.`;
    case 'letter_blocked':
      return `The virus check stopped ${first}’s letter. Generate the letters again.`;
    case 'letter_released':
      return `${first}’s letter has already gone out.`;
    case 'letter_flagged':
      return `${first}’s letter is marked Something’s wrong. Make it again, or clear it with a reason.`;
    default:
      return `Some of ${first}’s letter details need another look.`;
  }
}

/**
 * What the whole award needs, not one person: the award date and the template's signer. Each is
 * said once, at the control that fixes it, never once per person (QA-050).
 */
const AWARD_ISSUES: ReadonlySet<string> = new Set([
  'award_date_required',
  'signer_required',
  'signer_inactive',
]);

export function isAwardIssue(code: string): boolean {
  return AWARD_ISSUES.has(code);
}

/** Every issue code on a person's letter, from the view and from a refused call, once each. */
function lineCodes(line: Line, refused: ReadonlyMap<string, readonly string[]>): string[] {
  return [...new Set([...line.issues.map((i) => i.code), ...(refused.get(line.lineId) ?? [])])];
}

/** Every reason a person's letter can't go out yet that is theirs alone. */
export function lineProblems(
  line: Line,
  refused: ReadonlyMap<string, readonly string[]>,
): string[] {
  return lineProblemItems(line, refused).map((p) => p.words);
}

/** The same reasons with their codes, so the page can link one to where it's fixed. */
export function lineProblemItems(
  line: Line,
  refused: ReadonlyMap<string, readonly string[]>,
): { code: string; words: string }[] {
  return lineCodes(line, refused)
    .filter((code) => !isAwardIssue(code))
    .map((code) => ({ code, words: issueWords(code, line.person.name) }));
}

/** Where a missing tax is entered (APP-101): the words Letters links to Tax to enter. */
export const TAX_TO_ENTER_LINK = 'Enter it under Tax to enter';

/** "Maya's tax isn't entered yet.", before the link to Tax to enter. */
export function taxMissingWords(name: string): string {
  return `${firstName(name)}’s tax isn’t entered yet.`;
}

/** What the whole award needs before its letters can be made, each code once (QA-050). */
export function awardIssueCodes(
  letters: AwardLetters,
  refused: ReadonlyMap<string, readonly string[]>,
): string[] {
  return [...new Set(letters.lines.flatMap((l) => lineCodes(l, refused)).filter(isAwardIssue))];
}

/** A refused call whose every issue is about the whole award: it's said at its control instead. */
export function onlyAwardIssues(refused: ReadonlyMap<string, readonly string[]>): boolean {
  const codes = [...refused.values()].flat();
  return codes.length > 0 && codes.every(isAwardIssue);
}

/** A refused release or generation (`letters_not_ready`): the issue codes by person. */
export function refusedIssues(error: unknown): Map<string, string[]> {
  const out = new Map<string, string[]>();
  if (!isApiError(error) || error.code !== 'letters_not_ready') return out;
  for (const issue of error.problem?.issues ?? []) {
    const [lineId, field = ''] = issue.path.split('/');
    if (lineId === undefined || lineId === '') continue;
    const code =
      issue.code === DOUBLED_WORD && issue.message !== issue.code
        ? doubledWordCode(field, issue.message)
        : issue.code;
    out.set(lineId, [...(out.get(lineId) ?? []), code]);
  }
  return out;
}

/**
 * Letters a generation held back while it made the others (D22: a doubled word), by person, in the
 * same form as a refused call's issues, so each person's row says what their letter needs.
 */
export function heldIssues(letters: AwardLetters): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const issue of letters.held ?? []) {
    const code =
      issue.code === DOUBLED_WORD && issue.message !== issue.code
        ? doubledWordCode(issue.field ?? '', issue.message)
        : issue.code;
    out.set(issue.lineId, [...(out.get(issue.lineId) ?? []), code]);
  }
  return out;
}

/**
 * APP-244: what MyRewards' own run of the letters met, by person, as a refused Generate's issues
 * are: anyone's letter it held back, or why it made none. Only for letters still not made: a letter
 * made since answers it.
 */
export function automaticIssues(letters: AwardLetters): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const run = letters.automatic;
  if (run == null || run.status === 'waiting') return out;
  for (const issue of run.issues) {
    const line =
      issue.lineId === null ? undefined : letters.lines.find((l) => l.lineId === issue.lineId);
    if (line === undefined || line.letter !== null) continue;
    const code =
      issue.code === DOUBLED_WORD && issue.message !== undefined
        ? doubledWordCode(issue.field ?? '', issue.message)
        : issue.code;
    out.set(line.lineId, [...(out.get(line.lineId) ?? []), code]);
  }
  return out;
}

/** Two sets of issue codes by person, as one: each person's codes once each. */
export function mergedIssues(
  ...sets: readonly ReadonlyMap<string, readonly string[]>[]
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const set of sets)
    for (const [lineId, codes] of set)
      out.set(lineId, [...new Set([...(out.get(lineId) ?? []), ...codes])]);
  return out;
}

/** Why MyRewards made none of an award's letters, for what isn't one person's (APP-244). */
function automaticReason(code: string): string {
  switch (code) {
    case 'letter_converter_unavailable':
      return 'The service that turns Word letters into PDFs couldn’t be reached. Generate them in a few minutes; if it keeps happening, tell a System Admin.';
    case 'letter_conversion_failed':
      return 'A letter couldn’t be turned into a PDF. Check its template in Settings › Elections and letters, then generate again.';
    case 'letters_not_made':
      return 'Something went wrong while making them. Generate them yourself.';
    // SENT-135: held by "Not what you asked?" (APP-148 D17): nothing is made until it's settled.
    case 'award_held':
      return 'The award is on hold until its “Not what you asked?” question is settled. Once it is, generate the letters.';
    default:
      return issueWords(code, '');
  }
}

/**
 * APP-244: the Letters step's notice about MyRewards' own run of the letters: being made, or not
 * made with why and what to do next. Null once they're made: each letter then speaks for itself,
 * and a letter held back says why on its row.
 */
export function automaticNotice(
  letters: AwardLetters,
): { tone: 'waiting' | 'problem'; words: string } | null {
  const run = letters.automatic;
  if (run == null || run.status === 'made' || letters.released) return null;
  if (run.status === 'waiting')
    return {
      tone: 'waiting',
      words:
        'MyRewards is making these letters now. They show here once they’re made, usually within a minute: then read each one, change the wording or template if it needs it, and release them.',
    };
  const awardWide = [
    ...new Set(
      run.issues.filter((i) => i.lineId === null || isAwardIssue(i.code)).map((i) => i.code),
    ),
  ];
  const someone = run.issues.some((i) => i.lineId !== null && !isAwardIssue(i.code));
  return {
    tone: 'problem',
    words: [
      'MyRewards couldn’t make these letters on its own.',
      ...awardWide.map(automaticReason),
      ...(someone ? ['What each person’s letter needs is listed with it.'] : []),
      ...(awardWide.length === 0 || someone ? ['Fix it, then generate the letters.'] : []),
    ].join(' '),
  };
}

/**
 * What to open after a release (QA-050): the oldest award still waiting for a claim, never the
 * one just released (the queue may not have caught up yet).
 */
export function nextInQueue(rows: readonly QueueRow[], awardId: string): QueueRow | undefined {
  return rows.find((r) => r.claimedBy === null && r.award.id !== awardId);
}

/**
 * One person's letter that can be made again on its own: not made, out of date or blocked, marked
 * Something's wrong, or made from a fallback whose own letter is ready now (APP-130 D9, D11).
 * Never one waiting on a template: there's nothing to make it from yet.
 */
export function remakeable(line: Line): boolean {
  const state = personStateOf(line);
  if (state === 'waiting' || state === 'released') return false;
  return (
    line.status === 'missing' ||
    line.status === 'stale' ||
    line.status === 'blocked' ||
    state === 'something_wrong' ||
    line.makeAgain === true
  );
}

/** "Generate Isabel’s letter", "Make Isabel’s letter again", or "Generate … again". */
export function remakeLabel(line: Line): string {
  const first = firstName(line.person.name);
  if (line.letter === null) return `Generate ${first}’s letter`;
  if (line.makeAgain === true || personStateOf(line) === 'something_wrong')
    return `Make ${first}’s letter again`;
  return `Generate ${first}’s letter again`;
}

/** Plain words for a failed letters call. */
export function letterProblemWords(error: unknown): string {
  if (!isApiError(error)) return problemWords(error);
  switch (error.code) {
    case 'letters_not_ready':
      return error.status === 422
        ? 'Some letters can’t be made yet. What each person needs is listed with their letter.'
        : 'Some letters need attention before they can go out. What each person needs is listed with their letter.';
    case 'award_changed':
      return 'Something on the award changed while the letters were being made. Generate them again.';
    case 'award_date_changed':
      return 'The award date doesn’t match the letters. Generate them again with this date, then release.';
    case 'letter_template_missing':
      return 'There’s no letter template for this award type yet. Add one in Settings › Elections and letters.';
    case 'template_retired':
      return 'That template was retired. Pick another one.';
    case 'template_checking':
      return 'That template is still being checked for viruses. Try again in a minute.';
    case 'template_blocked':
      return 'That template can’t be used: the virus check stopped it. Pick another one.';
    case 'letter_converter_unavailable':
      return 'Letters from Word templates can’t be made right now. Nothing changed; try again later.';
    case 'letter_conversion_failed':
      return 'We couldn’t turn a letter into a PDF. Check the template, then try again.';
    case 'recipient_not_found':
      return 'Choose a letter holder who is active in the directory.';
    case 'award_date_needs_every_letter':
      return 'A new award date goes in every letter. Generate them all again with it.';
    case 'holder_reason_required':
      return 'Say why the letter goes to someone else. The reason goes in the award’s history.';
    case 'line_not_on_award':
      return 'Someone was removed from this award. We’ve loaded the latest; check it and try again.';
    case 'letter_released':
      return 'That letter has already gone out. Released letters never change.';
    case 'letters_all_released':
      return 'Every letter on this award has gone out.';
    case 'letter_not_current':
      return 'This letter was made again since you opened it. Read the new one, then review it.';
    case 'letter_flagged':
      return 'Someone marked this letter Something’s wrong. Make it again, or clear it with a reason.';
    case 'letter_nothing_to_clear':
      return 'Nobody marked this letter Something’s wrong, so there’s nothing to clear.';
    case 'award_date_locked':
      return 'Letters on this award have gone out with its award date, so the date stays as it is.';
    case 'letter_gap_closed':
      return 'The missing letter is ready now, so nothing needs to stand in. Make the letters again from it.';
    case 'letter_fallback_incompatible':
      return 'Choose an approved letter for this award type and these amounts.';
    case 'forbidden':
      return 'Only the person who holds the claim can make or release these letters.';
    default:
      return problemWords(error);
  }
}

// --- What the letter says --------------------------------------------------------------------------

/** The letter's facts in reading order: what the template fills in. */
const FACTS: readonly { field: string; label: string }[] = [
  { field: 'awardDate', label: 'Date' },
  { field: 'employeeName', label: 'Employee' },
  { field: 'employeeNumber', label: 'Employee #' },
  { field: 'awardName', label: 'Award' },
  { field: 'cash', label: 'Cash' },
  { field: 'options', label: 'Stock options' },
  { field: 'vesting', label: 'Vesting' },
  { field: 'electionDeadline', label: 'Choose by' },
  { field: 'signerName', label: 'Signed by' },
];

/** The filled-in values, labelled; options only when there are some. */
export function letterFacts(
  fields: Readonly<Record<string, string>> | null,
): { label: string; value: string }[] {
  if (fields === null) return [];
  return FACTS.flatMap(({ field, label }) => {
    const value = fields[field];
    if (value === undefined || value.trim() === '') return [];
    if (field === 'options' && value === '0') return [];
    return [{ label, value }];
  });
}

/** A PAA with cash: the estimated tax for each choice, as the letter's table shows it. */
export function taxRows(
  fields: Readonly<Record<string, string>> | null,
): { choice: string; estimate: string }[] {
  if (fields === null) return [];
  return (['A', 'B', 'C', 'D'] as const).flatMap((k) => {
    const choice = fields[`choice${k}`];
    const estimate = fields[`estimate${k}`];
    return choice === undefined || estimate === undefined ? [] : [{ choice, estimate }];
  });
}

/**
 * "Merge fields used": each fact the letter fills in, as the letter prints it, or where it comes
 * from before it can be worked out. A cash-only letter (a TAA, or a line with no options) prints
 * no options or vesting line, so neither shows here (QA-044).
 */
export function mergeSummary(letters: AwardLetters, line: Line | undefined): [string, string][] {
  const f = line?.mergeFields ?? null;
  const both = (a: string | undefined, b: string | undefined, fallback: string): string =>
    a !== undefined && b !== undefined ? `${a} · ${b}` : fallback;
  const cashOnly = letters.type === 'TAA' || line?.options === 0;
  // APP-246: a letter made without an employee number says so, rather than where it comes from.
  const employee =
    f?.['employeeName'] !== undefined && f['employeeNumber'] === undefined
      ? `${f['employeeName']} · no employee number`
      : both(f?.['employeeName'], f?.['employeeNumber'], 'From the directory');
  const rows: [string, string][] = [
    ['Employee · EE#', employee],
    cashOnly
      ? ['Cash', f?.['cash'] ?? 'From the check']
      : ['Cash · options', both(f?.['cash'], f?.['options'], 'From the check')],
  ];
  if (!cashOnly) rows.push(['Vesting', f?.['vesting'] ?? 'From the award settings']);
  if (letters.type === 'PAA' && (line === undefined || line.cashCents > 0))
    rows.push(['Tax table', 'From Tax to enter · Estimate']);
  rows.push(['Signer', f?.['signerName'] ?? 'From the template']);
  return rows;
}

// --- Who holds each letter -----------------------------------------------------------------------

const DEFAULT_WORDS: Record<AwardLetters['defaultRecipient'], string> = {
  requester: 'the requester',
  manager: 'the employee’s manager',
  employee: 'the employee',
};

export interface HolderChoice {
  id: string;
  name: string;
  /** "Requester · default for PAA", "The employee". */
  detail: string;
}

const ROLE_WORDS: Record<LetterHolderRole, (first: string) => string> = {
  employee: () => 'The employee',
  manager: (first) => `${first}’s supervisor`,
  requester: () => 'Requester',
  sector_lead: () => 'Sector Lead',
  delegate: () => 'Delegate',
};

/**
 * The people a letter can go to on this screen, as the server offers them (APP-067, from
 * APP-057's choices): whoever holds it now first, then the employee, their supervisor, the
 * requester and the sector's team, each saying why. Someone picked by name here is added last.
 */
export function holderChoices(
  letters: AwardLetters,
  line: Line,
  picked: readonly { id: string; name: string }[] = [],
): HolderChoice[] {
  const first = firstName(line.person.name);
  const r = line.recipient;
  const source =
    r === null
      ? null
      : r.source === 'default'
        ? `default for ${TYPE_SHORT[letters.type]}`
        : r.source === 'line'
          ? `chosen for ${first}`
          : 'chosen for this award';
  const out = line.holderChoices.map((c) => {
    const parts = [
      ...c.roles.map((role) => ROLE_WORDS[role](first)),
      ...(c.current && source !== null ? [source] : []),
    ];
    const detail = parts.length === 0 ? 'Holds it now' : parts.join(' · ');
    return { id: c.id, name: c.name, detail: capitalize(detail) };
  });
  const known = new Set(out.map((c) => c.id));
  return [
    ...out,
    ...picked
      .filter((p) => !known.has(p.id))
      .map((p) => ({ id: p.id, name: p.name, detail: 'Chosen here' })),
  ];
}

/** The default-holder setting's value in words ("The requester"), for its history. */
export function recipientWords(value: unknown): string {
  return typeof value === 'string' && value in DEFAULT_WORDS
    ? capitalize(DEFAULT_WORDS[value as AwardLetters['defaultRecipient']])
    : 'Not set';
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "Default for PAA: the requester." */
export function defaultHolderWords(letters: AwardLetters): string {
  return `Default for ${TYPE_SHORT[letters.type]}: ${DEFAULT_WORDS[letters.defaultRecipient]}`;
}

/** Holders chosen on this screen that differ from who holds each letter now. */
export function changedHolders(
  letters: AwardLetters,
  chosen: ReadonlyMap<string, string>,
): { lineId: string; recipientId: string }[] {
  return letters.lines.flatMap((l) => {
    const pick = chosen.get(l.lineId);
    return pick === undefined || pick === l.recipient?.id
      ? []
      : [{ lineId: l.lineId, recipientId: pick }];
  });
}

/**
 * The holders chosen here that replace one a line already had (chosen at entry, for the award, or
 * the type's default): "Imani Rowe → Jordan Ellis". Each needs a reason, audited on its line
 * (APP-056); filling in a missing holder doesn't.
 */
export function replacedHolders(
  letters: AwardLetters,
  chosen: ReadonlyMap<string, string>,
  holders: readonly ({ name: string } | null)[],
): { person: string; from: string; to: string }[] {
  return letters.lines.flatMap((l, i) => {
    const pick = chosen.get(l.lineId);
    const next = holders[i];
    if (pick === undefined || l.recipient === null || pick === l.recipient.id || next == null)
      return [];
    return [{ person: l.person.name, from: l.recipient.name, to: next.name }];
  });
}

/** The holder each letter goes to once released: the one chosen here, else the current one. */
export function holderOf(
  line: Line,
  chosen: ReadonlyMap<string, string>,
  choices: readonly HolderChoice[],
): { id: string; name: string } | null {
  const pick = chosen.get(line.lineId);
  if (pick !== undefined) {
    const found = choices.find((c) => c.id === pick);
    if (found !== undefined) return { id: found.id, name: found.name };
  }
  return line.recipient === null ? null : { id: line.recipient.id, name: line.recipient.name };
}

// --- Release ------------------------------------------------------------------------------------

/** A PAA with cash opens the employee's election when it's released. */
export function opensElection(
  letters: AwardLetters,
  lines: readonly Line[] = letters.lines,
): boolean {
  return letters.type === 'PAA' && lines.some((l) => l.cashCents > 0);
}

/**
 * A letter that can go out in the next release: the server says so; for an older answer, made,
 * ready, still to go and with nothing to fix.
 */
export function releasableNow(line: Line): boolean {
  if (line.releasable !== undefined) return line.releasable;
  return line.status === 'ready' && line.releasedAt === null && line.issues.length === 0;
}

/**
 * APP-130 3b: the letters that go out now (the ready ones), the ones that follow later to the
 * same holder, and the ones already out.
 */
export function releaseBatch(letters: AwardLetters): {
  now: Line[];
  following: Line[];
  released: Line[];
} {
  const toGo = letters.lines.filter((l) => l.releasedAt === null);
  return {
    now: toGo.filter(releasableNow),
    following: toGo.filter((l) => !releasableNow(l)),
    released: letters.lines.filter((l) => l.releasedAt !== null),
  };
}

/** Why a letter isn't going now, in a few words: "waits for the missing letter". */
export function followingWhy(line: Line): string {
  switch (personStateOf(line)) {
    case 'waiting':
      return 'waits for the missing letter';
    case 'missing':
      return 'isn’t made yet';
    case 'checking':
      return 'is being checked for viruses';
    case 'something_wrong':
      return 'is marked Something’s wrong';
    case 'stale':
      return 'is out of date';
    case 'blocked':
      return 'was stopped by the virus check';
    default:
      return 'needs another look';
  }
}

/** "2 of 3 released". */
export function releasedWords(letters: AwardLetters): string {
  const released = letters.lines.filter((l) => l.releasedAt !== null).length;
  return `${String(released)} of ${String(letters.lines.length)} released`;
}

/**
 * Why no letter can be released yet, in the order to fix them; empty when some can. Letters that
 * aren't ready don't stop the ready ones: they follow later (APP-130).
 */
export function releaseBlockers(
  letters: AwardLetters,
  dateDraft: string,
  holders: readonly ({ id: string } | null)[],
  yours: boolean,
): string[] {
  const out: string[] = [];
  if (!yours) out.push('Only the person who holds the claim can release these letters.');
  if (letters.awardDate === null || dateDraft === '')
    out.push('Set the award date with the template, then generate the letters.');
  else if (dateDraft !== letters.awardDate)
    out.push('The award date changed: generate the letters again with it.');
  const { now } = releaseBatch(letters);
  if (now.length === 0) {
    const toGo = letters.lines.filter((l) => l.releasedAt === null);
    const count = (...states: LetterPersonState[]): number =>
      toGo.filter((l) => states.includes(personStateOf(l))).length;
    // APP-272 (FB-2026-0035): a PAA's letter waits on its tax, and Generate can't help with that.
    const onTax = toGo.filter(
      (l) =>
        personStateOf(l) === 'missing' &&
        l.issues.some((issue) => issue.code === 'tax_estimates_missing'),
    ).length;
    if (onTax > 0)
      out.push(
        `${plural(onTax, 'person’s tax isn’t', 'people’s tax isn’t')} entered yet. ${TAX_TO_ENTER_LINK} first.`,
      );
    const missing = count('missing') - onTax;
    if (missing > 0)
      out.push(
        missing === letters.lines.length
          ? 'Generate the letters first.'
          : `${plural(missing, 'letter isn’t', 'letters aren’t')} made yet: generate the letters.`,
      );
    const stale = count('stale', 'blocked');
    if (stale > 0)
      out.push(
        `${plural(stale, 'letter needs', 'letters need')} making again: generate the letters.`,
      );
    const checking = count('checking');
    if (checking > 0)
      out.push(`${plural(checking, 'letter is', 'letters are')} still being checked for viruses.`);
    const waiting = count('waiting');
    if (waiting > 0)
      out.push(
        `${plural(waiting, 'letter waits', 'letters wait')} for a missing letter: see its card.`,
      );
    const wrong = count('something_wrong');
    if (wrong > 0)
      out.push(
        `${plural(wrong, 'letter is', 'letters are')} marked Something’s wrong: make ${wrong === 1 ? 'it' : 'them'} again or clear ${wrong === 1 ? 'it' : 'them'}.`,
      );
  }
  if (letters.lines.some((l, i) => releasableNow(l) && holders[i] == null))
    out.push('Choose who gets each letter.');
  return out;
}

/**
 * "Release letter", "Release 3 letters", or, with some to follow, "Release the 2 ready letters now";
 * once some are out and none of the rest is ready, "Release the last letter".
 */
export function releaseLabel(letters: AwardLetters): string {
  const { now, following, released } = releaseBatch(letters);
  // Every letter together, or none ready yet: the plain label.
  if (released.length === 0 && (following.length === 0 || now.length === 0))
    return letters.lines.length === 1
      ? 'Release letter'
      : `Release ${plural(letters.lines.length, 'letter', 'letters')}`;
  if (now.length === 0)
    return following.length === 1
      ? 'Release the last letter'
      : `Release the other ${plural(following.length, 'letter', 'letters')}`;
  if (now.length === 1) return 'Release the ready letter now';
  return `Release the ${plural(now.length, 'ready letter', 'ready letters')} now`;
}

/** The letters that follow later, by first name: "Ali’s letter follows later, to Imani Rowe." */
export function followingWords(
  letters: AwardLetters,
  holders: readonly ({ name: string } | null)[],
): string[] {
  return letters.lines.flatMap((l, i) => {
    if (l.releasedAt !== null || releasableNow(l)) return [];
    const holder = holders[i];
    return [
      `${firstName(l.person.name)}’s letter ${followingWhy(l)}. It follows later${holder ? `, to ${holder.name}` : ''}.`,
    ];
  });
}

/** The sentence under the release button: who gets what, and what follows. */
export function releaseSentence(
  letters: AwardLetters,
  holders: readonly ({ name: string } | null)[],
): string {
  const { now, following, released } = releaseBatch(letters);
  const going = new Set(now.map((l) => l.lineId));
  const names = [
    ...new Set(
      letters.lines.flatMap((l, i) => {
        const h = holders[i];
        return going.has(l.lineId) && h != null ? [h.name] : [];
      }),
    ),
  ];
  const all = following.length === 0 && released.length === 0;
  const [only] = now;
  const what = all
    ? letters.lines.length === 1
      ? 'the letter'
      : 'every letter'
    : now.length === 1 && only !== undefined
      ? `${firstName(only.person.name)}’s letter`
      : `the ${plural(now.length, 'ready letter', 'ready letters')}`;
  const to =
    names.length === 0
      ? 'Sends each letter to its holder.'
      : names.length === 1
        ? `Sends ${what} to ${names[0] ?? ''}.`
        : `Sends each letter to its holder (${names.join(', ')}).`;
  const who = now.length === 1 && only !== undefined ? firstName(only.person.name) : 'Each person';
  const sees = `${who} sees the award on the award date${opensElection(letters, now) ? ' and the election opens' : ''}.`;
  const rest =
    following.length === 0
      ? ''
      : ` ${plural(following.length, 'letter follows', 'letters follow')} later, to the same holder.`;
  return `${to} ${sees}${rest} Logged on the record.`;
}

/**
 * The confirm summary for releasing: the letters going now (every letter, or the ready ones), who
 * holds each, the ones that follow later, when the employee sees it (and the election, for a PAA
 * with cash), how many weren't reviewed (D10), and the claim ending.
 */
export function releaseSummary(
  letters: AwardLetters,
  holders: readonly ({ name: string } | null)[],
): { intro: string; rows: ConfirmSummaryRow[] } {
  const { now, following, released } = releaseBatch(letters);
  const all = following.length === 0 && released.length === 0;
  const date = letters.awardDate === null ? 'the award date' : formatDate(letters.awardDate);
  const n = now.length;
  const [only] = now;
  const going = new Set(now.map((l) => l.lineId));
  const rows: ConfirmSummaryRow[] = [
    {
      label: n === 1 ? 'Letter' : 'Letters',
      value:
        n === 1
          ? `${only?.person.name ?? 'One person'}’s letter`
          : all
            ? `All ${String(n)} together`
            : `${String(n)} now: ${andList(now.map((l) => firstName(l.person.name)))}`,
    },
  ];
  const byHolder = new Map<string, string[]>();
  letters.lines.forEach((l, i) => {
    const h = holders[i];
    if (!going.has(l.lineId) || h === null || h === undefined) return;
    byHolder.set(h.name, [...(byHolder.get(h.name) ?? []), firstName(l.person.name)]);
  });
  for (const [holder, people] of byHolder)
    rows.push({
      label: `${holder} gets`,
      value:
        n === 1
          ? 'An email now, with the letter to hand out'
          : `An email now, with ${people.length === n ? (all ? 'every letter' : 'these letters') : people.join(', ')} to hand out`,
    });
  if (following.length > 0)
    rows.push({
      label: 'Following later',
      value: `${andList(following.map((l) => firstName(l.person.name)))}: to the same holder, once ${following.length === 1 ? 'it’s' : 'they’re'} ready and released`,
    });
  rows.push({
    label: n === 1 ? `${firstName(only?.person.name ?? 'They')} sees it` : 'Each person sees it',
    value: `On ${date}, with an email, unless the holder releases it early`,
  });
  if (opensElection(letters, now)) {
    const deadline = now.find((l) => l.cashCents > 0)?.mergeFields?.['electionDeadline'];
    rows.push({
      label: 'Election',
      value:
        deadline === undefined
          ? `Opens on ${date}: cash, stock or a mix`
          : `Opens on ${date}; choose by ${deadline}`,
    });
  } else if (now.some((l) => l.cashCents > 0)) {
    rows.push({ label: 'Next', value: `With Payroll from ${date}` });
  }
  // APP-212: the release confirm names the letters that are the built-in sample.
  const samples = now.filter(isBuiltInSample).length;
  if (samples > 0)
    rows.push({
      label: 'The sample',
      value: `${plural(samples, 'letter is', 'letters are')} the built-in sample, marked “Sample - Please have an admin configure in Settings”`,
    });
  rows.push(reviewedRow(now));
  rows.push({
    label: 'Your claim',
    value:
      following.length === 0
        ? 'Ends: the award moves on'
        : 'Ends: the award goes back to the queue at Letters for the rest',
  });
  return {
    intro: all
      ? n === 1
        ? `The ${TYPE_LABEL[letters.type]} letter goes to its holder now, and the award moves on.`
        : 'Every letter goes out together, or none does, and the award moves on.'
      : `The ${n === 1 ? 'ready letter goes' : 'ready letters go'} to their holders now. ${plural(following.length, 'letter follows', 'letters follow')} later, to the same holder; the award date stays as it is.`,
    rows,
  };
}

/**
 * D10: the release confirm says how many of the letters going out haven't been reviewed.
 * Reviewing is encouraged, never required, so this informs and never blocks.
 */
export function reviewedRow(lines: readonly Line[]): ConfirmSummaryRow {
  const read = lines.filter((l) => personStateOf(l) === 'reviewed').length;
  const unread = lines.length - read;
  return {
    label: 'Reviewed',
    value:
      unread === 0
        ? lines.length === 1
          ? 'Yes'
          : `All ${String(lines.length)}`
        : lines.length === 1
          ? 'Not reviewed yet. It can go as it is.'
          : `${String(read)} of ${String(lines.length)}: ${plural(unread, 'letter hasn’t', 'letters haven’t')} been reviewed. They can go as they are.`,
  };
}

/** "Generated at 9:58 AM from PAA template v3 · 1 letter". */
export function generatedWords(letters: AwardLetters): string | null {
  const made = letters.lines.flatMap((l) => (l.letter === null ? [] : [l.letter]));
  const [latest] = [...made].sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
  if (latest === undefined) return null;
  const by =
    latest.generatedBy !== null
      ? ` by ${latest.generatedBy.name}`
      : latest.automatic === true
        ? ' by MyRewards, automatically,'
        : '';
  const from =
    latest.template === null
      ? builtInSampleName(latest.builtInSample?.version ?? 1)
      : templateLabel(latest.template, false);
  return `Made ${formatDate(latest.generatedAt)}${by} from ${from} · ${plural(made.length, 'letter', 'letters')}`;
}

/** APP-212: "the built-in sample letter v1", as a made letter names what it was made from. */
export function builtInSampleName(version: number): string {
  return `the built-in sample letter v${String(version)}`;
}

/**
 * APP-212: whether a person's letter is the built-in sample letter: the one made, or with none
 * made yet, the one Generate would make (no real letter fits their signer and amounts).
 */
export function isBuiltInSample(line: Line): boolean {
  return line.letter !== null ? line.letter.builtInSample != null : line.builtInSample != null;
}

/**
 * APP-212: the Letters step's notice while any letter still to go is the built-in sample. Staff
 * see it said plainly, with the words the letter itself prints; null when no letter is the sample.
 */
export function builtInSampleNotice(letters: AwardLetters): string | null {
  const toGo = letters.lines.filter((l) => l.releasedAt === null);
  const sample = toGo.filter(isBuiltInSample);
  if (sample.length === 0) return null;
  const head =
    sample.length === toGo.length
      ? toGo.length === 1
        ? 'This award’s letter is the sample.'
        : 'This award’s letters are the sample.'
      : `${plural(sample.length, 'letter', 'letters')} on this award ${sample.length === 1 ? 'is' : 'are'} the sample: ${andList(sample.map((l) => firstName(l.person.name)))}.`;
  return `${head} No real letter is set up for ${sample.length === 1 ? 'it' : 'them'} yet, so MyRewards uses its built-in sample letter, signed in the signer’s name and marked “Sample - Please have an admin configure in Settings”. Upload the real letter in Settings › Elections and letters, then make it again.`;
}
